import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import InfoPage from "../InfoPage";
import MembershipBadge from '../../components/MembershipBadge';
import FounderBadge from '../../components/FounderBadge';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { getAuthToken, decodeToken } from '../../utils/authHelpers';
import { authFetch } from '../../utils/authFetch';
import { displayHandle as fmtHandle } from '../../utils/streamHelpers';
import { uploadFile } from '../../utils/fileUpload';
import { getCloudinaryUrl } from '../../utils/cloudinary';
import ModalPortal from '../../components/Modal/ModalPortal';
import BoardComments from '../../components/BoardComments';
import BlurredGate from '../../components/BlurredGate';
import Dropdown from '../../components/Dropdown';
import { usePersona } from '../../contexts/PersonaContext';
import IntroVideoPreview from '../../components/IntroVideoPreview';
import './TravelBoard.sass';

const parseEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '').replace(/^m\./, '');

    // YouTube: youtube.com/watch, youtube.com/shorts, youtu.be
    if (host === 'youtube.com' || host === 'youtu.be') {
      let videoId = null;
      if (host === 'youtu.be') {
        videoId = u.pathname.slice(1).split('/')[0];
      } else if (u.pathname.startsWith('/shorts/')) {
        videoId = u.pathname.replace('/shorts/', '').split('/')[0];
      } else if (u.pathname === '/watch') {
        videoId = u.searchParams.get('v');
      } else if (u.pathname.startsWith('/embed/')) {
        videoId = u.pathname.replace('/embed/', '').split('/')[0];
      }
      if (videoId) return { provider: 'youtube', embedUrl: `https://www.youtube.com/embed/${videoId}` };
    }

    // TikTok: tiktok.com/@user/video/ID
    if (host === 'tiktok.com' || host.endsWith('.tiktok.com')) {
      const videoMatch = u.pathname.match(/\/video\/(\d+)/);
      if (videoMatch) return { provider: 'tiktok', embedUrl: `https://www.tiktok.com/embed/v2/${videoMatch[1]}` };
    }

    // Instagram: instagram.com/reel/CODE or /reels/CODE or /p/CODE
    if (host === 'instagram.com') {
      const reelMatch = u.pathname.match(/\/(reel|reels|p)\/([A-Za-z0-9_-]+)/);
      if (reelMatch) return { provider: 'instagram', embedUrl: `https://www.instagram.com/p/${reelMatch[2]}/embed` };
    }

    return null;
  } catch (_e) { return null; }
};

function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

const HeartIcon = ({ isLiked, size = 14 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: 'inline-block', verticalAlign: 'middle' }}
  >
    <path 
      fillRule="evenodd" 
      clipRule="evenodd" 
      d="M8.55284 3.00012C7.93598 3.00012 7.23841 3.06514 6.57209 3.29224C2.55494 4.60387 1.26341 8.894 2.39877 12.43L2.40354 12.4448L2.40877 12.4595C3.03435 14.2174 4.04226 15.8127 5.35336 17.1249L5.36091 17.1324L5.36862 17.1398C7.23782 18.9323 9.27254 20.4953 11.4756 21.8515L11.9934 22.1703L12.5147 21.8573C14.7226 20.5315 16.7964 18.9254 18.6432 17.1474L18.649 17.1419L18.6547 17.1362C19.9771 15.8215 20.9851 14.2144 21.6015 12.4549L21.6066 12.4402L21.6113 12.4253C22.7251 8.89703 21.4401 4.60176 17.4507 3.30948C16.7976 3.09221 16.1236 3.00012 15.4648 3.00012C13.9828 3.00011 12.8858 3.62064 12.0004 4.25309C11.1219 3.62545 10.0176 3.00012 8.55284 3.00012Z" 
      fill={isLiked ? '#dc1d08' : '#999'}
    />
  </svg>
);

const Card = ({
  img,
  handle,
  onClick,
  likeCount = 0,
  isLiked = false,
  travelPostId,
  onLikeToggle,
  introVideoUrl,
  introVideoPosterUrl,
  introVideoPublicId,
}) => {
  const optimizedImg = img ? getCloudinaryUrl(img, { width: 450, height: 600, crop: 'fill', quality: 'auto', format: 'auto' }) : null;

  const handleHeartClick = async (e) => {
    e.stopPropagation(); // Prevent card click
    if (!onLikeToggle) return;
    
    const token = getAuthToken();
    if (!token) {
      alert('Please log in to like travel opportunities');
      return;
    }
    
    try {
      const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      const payload = { travel_post_id: travelPostId };
      
      const res = await authFetch(`${base}/api/collab/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      if (res.ok && data.ok) {
        onLikeToggle(data.liked, data.like_count);
      } else {
        console.error('Failed to toggle like');
      }
    } catch (err) {
      // Don't log auth errors since authFetch handles logout
      if (!err.message?.includes('Unauthorized') && !err.message?.includes('expired')) {
        console.error('Like toggle error:', err);
      }
    }
  };

  return (
    <div 
      className="travel-board-card"
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
    >
      {optimizedImg ? (
        <div className="travel-board-image-wrapper">
          {introVideoUrl ? (
            <IntroVideoPreview
              mode="card"
              posterUrl={introVideoPosterUrl}
              publicId={introVideoPublicId}
              fullUrl={introVideoUrl}
              fallbackImageUrl={optimizedImg}
              alt={handle}
            />
          ) : (
            <img
              src={optimizedImg}
              alt={handle}
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
              onError={(e) => {
                // Hide image if it fails to load - don't show fallback
                e.currentTarget.style.display = 'none';
              }}
              onMouseEnter={(e) => { if (onClick) { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.12)'; }}}
              onMouseLeave={(e) => { if (onClick) { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,.08)'; }}}
            />
          )}
        </div>
      ) : null}
      <div className="travel-board-card-name">
        <span>{handle}</span>
        <span 
          className="travel-board-card-like"
          onClick={handleHeartClick}
        >
          <HeartIcon isLiked={isLiked} size={14} />
          {likeCount > 0 && (
            <span style={{ 
              fontSize: '12px', 
              color: isLiked ? '#dc1d08' : '#999',
              fontFamily: "'Neue Einstellung', 'Poppins', sans-serif"
            }}>
              {likeCount}
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

const NavigationButton = ({ direction, onClick, show }) => {
  if (!show) return null;
  
  return (
    <motion.button
      onClick={onClick}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className={`travel-board-nav-button ${direction === 'left' ? 'nav-left' : 'nav-right'}`}
    >
      <svg 
        width="16" 
        height="16" 
        viewBox="0 0 16 16" 
        fill="none" 
        style={{ transform: direction === 'left' ? 'rotate(180deg)' : 'none' }}
      >
        <path 
          d="M6 4L10 8L6 12" 
          stroke="white" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        />
      </svg>
    </motion.button>
  );
};

const Modal = ({ isOpen, onClose, title, content, image, planId, planName, messageUserId, propertyId, travelPost, travelPostId, travelPostType, travelDetails, isOwner, onDelete, onEdit, handle, profile_id, host_profile_id, isCreator, airbnbUrl, vrboUrl, directBookingUrl, founder, focusCommentId }) => {
  const [isPortrait, setIsPortrait] = useState(null);

  // Reset state when modal closes or image changes
  useEffect(() => {
    if (!isOpen || !image) {
      setIsPortrait(null);
    }
  }, [isOpen, image]);

  // Get logged-in user's type from localStorage
  const loggedInUserType = typeof window !== 'undefined' 
    ? window.localStorage.getItem('postaway_user_type') || '' 
    : '';
  
  // Hide Message button for travelers
  const canMessage = loggedInUserType !== 'traveler';

  // Helper function to render travel details
  const renderTravelDetails = (details, postType) => {
    if (!details) return null;
    
    const sections = [];
    
    // Post type label
    const typeLabels = {
      'group_trip': 'Group Trip',
      'substack': 'Substack / Newsletter / Blog',
      'podcast': 'Podcast',
      'upcoming_event': 'Upcoming Event',
      'last_minute': 'Last Minute Availability',
      'discount_code': 'Discount Codes',
      'unique_destination': 'Unique Destination',
      'follow_me': 'Follow Me (Intro)',
      'storefront': 'Storefront or Linktree',
      'new_itinerary': 'New Itinerary / Digital Guidebook / Masterclass / Podcast Episode / Storefront',
      'general': 'General Announcement',
      'other': 'Other'
    };
    
    if (postType && typeLabels[postType]) {
      sections.push(
        <div key="type" className="travel-detail-item">
          <strong>Type:</strong> {typeLabels[postType]}
        </div>
      );
    }
    
    // Group Trip specific
    if (postType === 'group_trip' && details.groupTrip) {
      const gt = details.groupTrip;
      if (gt.location) {
        sections.push(
          <div key="gt-location" className="travel-detail-item">
            <strong>Trip Location:</strong> {gt.location}
          </div>
        );
      }
      if (gt.dateStart && gt.dateEnd) {
        sections.push(
          <div key="gt-dates" className="travel-detail-item">
            <strong>Dates:</strong> {new Date(gt.dateStart).toLocaleDateString()} to {new Date(gt.dateEnd).toLocaleDateString()}
          </div>
        );
      }
      if (gt.availableSpots) {
        sections.push(
          <div key="gt-spots" className="travel-detail-item">
            <strong>Available Spots:</strong> {gt.availableSpots}
          </div>
        );
      }
      if (gt.priceMin || gt.priceMax) {
        const min = gt.priceMin ? `$${gt.priceMin}` : '';
        const max = gt.priceMax ? `$${gt.priceMax}` : '';
        sections.push(
          <div key="gt-price" className="travel-detail-item">
            <strong>Price Range:</strong> {min}{min && max ? ' - ' : ''}{max} per person
          </div>
        );
      }
      if (gt.hostedCount) {
        sections.push(
          <div key="gt-hosted" className="travel-detail-item">
            <strong>Group Trips Hosted:</strong> {gt.hostedCount}
          </div>
        );
      }
      if (gt.contact) {
        sections.push(
          <div key="gt-contact" className="travel-detail-item">
            <strong>Contact:</strong> {gt.contact}
          </div>
        );
      }
      if (gt.durationDays) {
        sections.push(
          <div key="gt-duration" className="travel-detail-item">
            <strong>Duration:</strong> {gt.durationDays} days
          </div>
        );
      }
      if (gt.signupUrl) {
        sections.push(
          <div key="gt-signup" className="travel-detail-item">
            <strong>Sign Up:</strong>{' '}
            <a href={gt.signupUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#dc1d08', textDecoration: 'none', fontWeight: 600 }}>
              {gt.signupUrl}
            </a>
          </div>
        );
      }
    }
    
    // New Itinerary specific
    if (postType === 'new_itinerary' && details.newItinerary) {
      const ni = details.newItinerary;
      if (ni.location) {
        sections.push(
          <div key="ni-location" className="travel-detail-item">
            <strong>Itinerary Location:</strong> {ni.location}
          </div>
        );
      }
      if (ni.days) {
        sections.push(
          <div key="ni-days" className="travel-detail-item">
            <strong>Duration:</strong> {ni.days} days
          </div>
        );
      }
      if (ni.favoritePart) {
        sections.push(
          <div key="ni-favorite" className="travel-detail-item">
            <strong>Favorite Part:</strong> {ni.favoritePart}
          </div>
        );
      }
    }
    
    // Unique Destination specific
    if (postType === 'unique_destination' && details.uniqueDestination) {
      const ud = details.uniqueDestination;
      if (ud.recommendation) {
        sections.push(
          <div key="ud-rec" className="travel-detail-item">
            <strong>Recommendation:</strong> {ud.recommendation}
          </div>
        );
      }
    }
    
    // Follow Me specific
    if (postType === 'follow_me' && details.followMe) {
      const fm = details.followMe;
      if (fm.cta) {
        sections.push(
          <div key="fm-cta" className="travel-detail-item">
            {fm.cta}
          </div>
        );
      }
    }

    // Storefront / Linktree specific
    if (postType === 'storefront' && details.storefront) {
      const sf = details.storefront;
      if (sf.url) {
        sections.push(
          <div key="sf-url" className="travel-detail-item">
            <strong>Storefront:</strong>{' '}
            <a href={sf.url} target="_blank" rel="noopener noreferrer">{sf.url}</a>
          </div>
        );
      }
    }
    
    // Additional notes
    if (details.additionalNotes) {
      sections.push(
        <div key="notes" className="travel-detail-item">
          <strong>Additional Notes:</strong> {details.additionalNotes}
        </div>
      );
    }

    // Media (reel video or external link)
    if (details.media) {
      const m = details.media;
      if (m.reelVideoUrl) {
        sections.push(
          <div key="media-video" className="travel-modal-media-section">
            <video src={m.reelVideoUrl} controls playsInline preload="metadata" />
          </div>
        );
      }
      if (m.externalUrl) {
        const embed = parseEmbedUrl(m.externalUrl);
        if (embed) {
          const aspectRatio = embed.provider === 'youtube' ? '56.25%' : '177.78%';
          sections.push(
            <div key="media-embed" className="travel-modal-media-section" style={{ position: 'relative', width: '100%', paddingBottom: aspectRatio, maxHeight: embed.provider === 'youtube' ? 'none' : 520, overflow: 'hidden', borderRadius: 8 }}>
              <iframe
                src={embed.embedUrl}
                title={`${embed.provider} embed`}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
          );
        } else {
          const urlLabel = (() => {
            try {
              const host = new URL(m.externalUrl).hostname.replace(/^www\./, '');
              return host;
            } catch (_e) { return 'View Link'; }
          })();
          sections.push(
            <a key="media-link" href={m.externalUrl} target="_blank" rel="noopener noreferrer" className="travel-modal-external-link">
              <svg className="external-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              <span className="external-link-text">{urlLabel}</span>
            </a>
          );
        }
      }
    }
    
    return sections.length > 0 ? <div className="travel-details-section">{sections}</div> : null;
  };

  if (!title) return null;

  // Handle image load to detect aspect ratio
  const handleImageLoad = (e) => {
    const img = e.target;
    if (img.naturalWidth && img.naturalHeight) {
      setIsPortrait(img.naturalWidth < img.naturalHeight);
    }
  };

  // Handle image error - default to landscape behavior
  const handleImageError = (e) => {
    setIsPortrait(false);
  };

  // Only show blurred sides for creator portrait images
  const shouldShowBlur = isCreator && isPortrait === true;
  
  return (
    <ModalPortal isOpen={isOpen} onClose={onClose} maxWidth="600px" maxHeight="80vh">
        {image && (
          <div className="travel-modal-header" style={{ 
            justifyContent: shouldShowBlur ? 'center' : 'stretch'
          }}>
            {/* Blurred background layer - only for creator portrait images */}
            {shouldShowBlur && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                filter: 'blur(20px)',
                transform: 'scale(1.1)',
                zIndex: 0
              }} />
            )}
            
            {/* Main image */}
            <img 
              src={image} 
              alt={title}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{
                position: 'relative',
                zIndex: 1,
                width: shouldShowBlur ? 'auto' : '100%',
                height: shouldShowBlur ? '100%' : '100%',
                maxWidth: shouldShowBlur ? '100%' : 'none',
                maxHeight: shouldShowBlur ? '100%' : 'none',
                objectFit: shouldShowBlur ? 'contain' : 'cover'
              }}
            />
          </div>
        )}
        
        <div className="travel-modal-content">
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 16
          }}>
            <h2 className="travel-modal-title">
              {title}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FounderBadge isFounder={founder} />
              <MembershipBadge planId={planId} planName={planName} userType="host" />
            </div>
          </div>
          
          <div className="travel-modal-description">
            {travelPost ? (
              <>
                <div style={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 16
                }}>
                  <h4 style={{
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                    fontSize: 14,
                    color: '#0f084b',
                    marginTop: 0,
                    marginBottom: 8,
                    opacity: 0.7
                  }}>Travel Board Post:</h4>
                  <div style={{
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                    fontSize: 15,
                    color: '#0f084b',
                    lineHeight: 1.5
                  }}>
                    {travelPost}
                  </div>
                </div>
                
                {/* Display structured travel details for creators */}
                {travelDetails && renderTravelDetails(travelDetails, travelPostType)}
                
                {content && (
                  <div>
                    <h4 style={{
                      fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                      fontSize: 14,
                      color: '#0f084b',
                      marginBottom: 8,
                      opacity: 0.7
                    }}>Property Details:</h4>
                    <div>{content}</div>
                  </div>
                )}
              </>
            ) : (
              content || 'No details provided yet.'
            )}
          </div>
          
          <div className="travel-modal-actions">
            <div className="travel-modal-buttons">
              {isOwner && (
                <>
                  <button
                    className="button-edit"
                    onClick={onEdit}
                    style={{
                      padding: '10px 20px',
                      border: '2px solid #0f084b',
                      borderRadius: '8px',
                      backgroundColor: '#fff',
                      color: '#0f084b',
                      fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#0f084b';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#fff';
                      e.currentTarget.style.color = '#0f084b';
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="button-delete"
                    onClick={() => {
                      if (window.confirm('Are you sure you want to delete this travel opportunity?')) {
                        onDelete();
                      }
                    }}
                  >
                    Delete
                  </button>
                </>
              )}
              {!isOwner && canMessage && (
                <button
                  className="button-request"
                  onClick={() => {
                    if (messageUserId) {
                      const params = new URLSearchParams();
                      params.set('to', messageUserId);
                      if (title) params.set('title', title);
                      window.location.href = `/messages?${params.toString()}`;
                    } else {
                      window.location.href = '/messages';
                    }
                  }}
                >
                  {isCreator ? 'Message Creator' : 'Message Host'}
                </button>
              )}
              
              <button
                className="button-profile"
                onClick={() => {
                  const params = new URLSearchParams();
                  if (isCreator && profile_id) {
                    // Creator with ID
                    params.set('creator_id', profile_id);
                  } else if (isCreator && handle) {
                    // Creator with handle only
                    params.set('handle', handle);
                    params.set('creator', '1');
                  } else if (host_profile_id) {
                    // Host with ID (preferred)
                    params.set('host_id', host_profile_id);
                    if (propertyId) {
                      params.set('property_id', propertyId);
                    }
                  } else if (propertyId) {
                    // Host with property_id only (fallback)
                    params.set('property_id', propertyId);
                  } else if (handle) {
                    // Fallback to handle
                    params.set('handle', handle);
                  }
                  window.location.href = `/profile?${params.toString()}`;
                }}
              >
                View Profile
              </button>
            </div>
            
            {/* Booking Links - Icons on the right */}
            {(airbnbUrl || vrboUrl || directBookingUrl) && (
              <div className="travel-modal-booking-links">
                {directBookingUrl && (
                  <Tippy content="Book directly with the host and save">
                  <a
                    href={directBookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'opacity 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    <img src="/circle-p-logo.png" alt="Direct" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                  </a>
                  </Tippy>
                )}
                {vrboUrl && (
                  <a
                    href={vrboUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'opacity 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    <img src="/vrbo logo.png" alt="VRBO" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                  </a>
                )}
                {airbnbUrl && (
                  <a
                    href={airbnbUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'opacity 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    <img src="/airbnb_logo.png" alt="Airbnb" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                  </a>
                )}
              </div>
            )}
          </div>
          
          {/* Comments Section */}
          {travelPostId && (
            <BoardComments
              target={{ kind: 'travel_post', id: travelPostId }}
              postOwnerUserId={messageUserId}
              isOpen={isOpen}
              focusCommentId={focusCommentId}
            />
          )}
        </div>
    </ModalPortal>
  );
};

const TravelBoard = () => {
  const { activePersona } = usePersona();
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [email, setEmail] = useState('');
  const [data, setData] = useState({ hosts: [], creators: [] });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedHost, setSelectedHost] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [hostStartIndex, setHostStartIndex] = useState(0);
  const [creatorStartIndex, setCreatorStartIndex] = useState(0);
  const [hostDirection, setHostDirection] = useState(1); // -1 for back, 1 for forward
  const [creatorDirection, setCreatorDirection] = useState(1); // -1 for back, 1 for forward
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [postModalOpen, setPostModalOpen] = useState(false);
  const [postText, setPostText] = useState('');
  const [userType, setUserType] = useState(null); // 'host' | 'creator'
  const [postPropertyId, setPostPropertyId] = useState('');
  const [userRoles, setUserRoles] = useState([]);
  const [userProperties, setUserProperties] = useState([]);
  const [canPost, setCanPost] = useState(false);
  const [focusCommentId, setFocusCommentId] = useState(null);
  const [membershipTier, setMembershipTier] = useState(null);
  
  // Creator travel post enhanced fields
  const [travelPostType, setTravelPostType] = useState(''); // 'group_trip' | 'new_itinerary' | 'unique_destination' | 'follow_me' | 'storefront' | 'other'
  const [additionalNotes, setAdditionalNotes] = useState('');
  // Group Trip fields
  const [groupTripLocation, setGroupTripLocation] = useState('');
  const [groupTripDateStart, setGroupTripDateStart] = useState('');
  const [groupTripDateEnd, setGroupTripDateEnd] = useState('');
  const [groupTripSpots, setGroupTripSpots] = useState('');
  const [groupTripPriceMin, setGroupTripPriceMin] = useState('');
  const [groupTripPriceMax, setGroupTripPriceMax] = useState('');
  const [groupTripHostedCount, setGroupTripHostedCount] = useState('');
  const [groupTripContact, setGroupTripContact] = useState('');
  const [groupTripDurationDays, setGroupTripDurationDays] = useState('');
  const [groupTripSignupUrl, setGroupTripSignupUrl] = useState('');
  // Unique Destination fields
  const [destinationRecommendation, setDestinationRecommendation] = useState('');
  // Follow Me fields
  const [followMeCta, setFollowMeCta] = useState('');
  // Storefront / Linktree fields
  const [storefrontUrl, setStorefrontUrl] = useState('');
  
  // Shared media fields (hero image + reel/link for all announcement types)
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [heroImagePreview, setHeroImagePreview] = useState('');
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [reelVideoFile, setReelVideoFile] = useState(null);
  const [reelVideoPreview, setReelVideoPreview] = useState('');
  const [reelVideoUrl, setReelVideoUrl] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [mediaSourceType, setMediaSourceType] = useState('link'); // 'upload' | 'link'
  const [uploadProgress, setUploadProgress] = useState('');
  
  // View All toggle state
  const [showAllHosts, setShowAllHosts] = useState(false);
  const [showAllCreators, setShowAllCreators] = useState(false);
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editPostId, setEditPostId] = useState(null);
  
  // Post expiration
  const [postExpirationType, setPostExpirationType] = useState('indefinitely'); // 'date' | 'indefinitely'
  const [postExpirationDate, setPostExpirationDate] = useState('');
  
  const [selectedCategory, setSelectedCategory] = useState('');
  
  const [searchParams] = useSearchParams();
  
  // Sync URL filter param into state on mount
  useEffect(() => {
    const urlFilter = searchParams.get('filter') || '';
    if (urlFilter) setSelectedCategory(urlFilter);
  }, [searchParams]);
  
  const activeFilter = selectedCategory;
  
  const filterLabels = {
    group_trips: 'Group Trips',
    substack: 'Substack / Newsletter / Blog',
    podcast: 'Podcast',
    last_minute: 'Last Minute Availability',
    discount_codes: 'Discount Codes',
    upcoming_events: 'Upcoming Events',
    unique_destination: 'Unique Destination',
    follow_me: 'Follow Me (Intro)',
    new_itinerary: 'Itineraries',
    general: 'General Announcement',
  };
  
  // Mirrors the header's Travel nav dropdown. "Itineraries" lives on its own
  // page (/itineraries) rather than being a travel-post filter.
  const categoryOptions = [
    'All Opportunities',
    'Itineraries',
    'Group Trips',
    'Substack / Newsletter / Blog',
    'Podcast',
    'Last Minute Availability',
    'Discount Codes',
    'Upcoming Events',
    'General Announcement',
  ];
  const categoryLabelToKey = Object.fromEntries(
    Object.entries(filterLabels).map(([k, v]) => [v, k])
  );
  const selectedCategoryLabel = filterLabels[selectedCategory] || 'All Opportunities';
  
  const handleCategoryChange = (label) => {
    if (label === 'Itineraries') {
      navigate('/itineraries');
      return;
    }
    const key = categoryLabelToKey[label] || '';
    setSelectedCategory(key);
  };
  
  const filterLabel = filterLabels[activeFilter] || '';
  
  const matchesFilter = (item, isHost) => {
    if (!activeFilter) return false;
    const postType = (item.travel_post_type || '').toLowerCase();
    const content = (item.travel_post || '').toLowerCase();
    const details = typeof item.travel_details === 'string'
      ? item.travel_details.toLowerCase()
      : JSON.stringify(item.travel_details || {}).toLowerCase();
    
    const typeToFilter = {
      'group_trip': 'group_trips',
      'substack': 'substack',
      'podcast': 'podcast',
      'last_minute': 'last_minute',
      'discount_code': 'discount_codes',
      'upcoming_event': 'upcoming_events',
      'unique_destination': 'unique_destination',
      'follow_me': 'follow_me',
      'storefront': 'storefront',
      'new_itinerary': 'new_itinerary',
      'general': 'general',
      'other': null,
    };

    if (postType && postType in typeToFilter) {
      return typeToFilter[postType] === activeFilter;
    }

    switch (activeFilter) {
      case 'group_trips':
        return postType === 'group_trip';
      case 'substack':
        return postType === 'substack' || content.includes('substack') || content.includes('newsletter') || content.includes('blog') || details.includes('substack') || details.includes('newsletter') || details.includes('blog');
      case 'podcast':
        return postType === 'podcast' || content.includes('podcast') || details.includes('podcast');
      case 'last_minute':
        return postType === 'last_minute' || content.includes('last minute') || content.includes('availability')
          || details.includes('last minute') || details.includes('availability');
      case 'discount_codes':
        return postType === 'discount_code' || content.includes('% off') || content.includes('code:') || content.includes('use code')
          || content.includes('discount code') || content.includes('promo') || content.includes('coupon');
      case 'upcoming_events':
        return postType === 'upcoming_event' || content.includes('event') || details.includes('event')
          || content.includes('festival') || details.includes('festival')
          || content.includes('oktoberfest') || details.includes('oktoberfest')
          || content.includes('parade') || details.includes('parade')
          || content.includes('celebration') || details.includes('celebration')
          || content.includes('concert') || details.includes('concert')
          || content.includes('lighting') || details.includes('lighting')
          || content.includes('race') || details.includes('race')
          || content.includes('fair') || details.includes('fair')
          || content.includes('tournament') || details.includes('tournament')
          || content.includes('giveaway') || details.includes('giveaway')
          || /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+\d{1,2}/i.test(item.travel_post || '')
          || /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+\d{1,2}/i.test(JSON.stringify(item.travel_details || ''));
      case 'unique_destination':
        return postType === 'unique_destination' || content.includes('unique destination') || content.includes('hidden gem') || details.includes('recommendation');
      case 'follow_me':
        return postType === 'follow_me' || content.includes('follow me') || content.includes('follow along') || details.includes('followMe');
      case 'storefront':
        return postType === 'storefront' || content.includes('storefront') || content.includes('linktree') || content.includes('shopmy') || content.includes('ltk') || details.includes('storefront');
      case 'new_itinerary':
        return postType === 'new_itinerary' || content.includes('itinerary') || content.includes('guidebook') || content.includes('masterclass') || details.includes('newItinerary');
      case 'general':
        return postType === 'general';
      default:
        return false;
    }
  };
  
  const filteredHosts = activeFilter ? data.hosts.filter(h => matchesFilter(h, true)) : [];
  const filteredCreators = activeFilter ? data.creators.filter(c => matchesFilter(c, false)) : [];
  const filteredItems = [...filteredHosts.map(h => ({...h, isHost: true})), ...filteredCreators.map(c => ({...c, isCreator: true}))];
  
  const addButtonLabel = activeFilter && filterLabel
    ? `Add a ${filterLabel} Opportunity`
    : 'Add a Travel Opportunity/Announcement';
  
  // Track property views when modal opens with a property
  useEffect(() => {
    if (modalOpen && selectedHost?.property_id && selectedHost?.user_id !== currentUserId) {
      // Only track if it's not the owner viewing their own property
      const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      fetch(`${apiBase}/api/properties/track-view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_id: selectedHost.property_id })
      }).catch(err => console.error('Failed to track property view:', err));
    }
  }, [modalOpen, selectedHost?.property_id, selectedHost?.user_id, currentUserId]);

  // Track creator profile views when modal opens with a creator
  useEffect(() => {
    if (modalOpen && selectedHost?.isCreator && selectedHost?.profile_id && selectedHost?.user_id !== currentUserId) {
      // Only track if it's not the owner viewing their own profile
      const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      fetch(`${apiBase}/api/creator/track-view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ creator_profile_id: selectedHost.profile_id })
      }).catch(err => console.error('Failed to track creator profile view:', err));
    }
  }, [modalOpen, selectedHost?.isCreator, selectedHost?.profile_id, selectedHost?.user_id, currentUserId]);

  // Extract Instagram handle from URL
  const extractInstagramHandle = (url) => {
    if (!url) return null;
    try {
      // Handle various Instagram URL formats
      // Examples: 
      // https://www.instagram.com/username
      // https://instagram.com/username/
      // https://www.instagram.com/username?utm_source=...
      // https://www.instagram.com/username&source=google
      
      // Remove protocol and www
      let cleaned = url.replace(/^https?:\/\/(www\.)?/, '');
      
      // Check if it's an Instagram URL
      if (!cleaned.startsWith('instagram.com/')) {
        // Try to extract from the full URL if it contains instagram.com
        const match = url.match(/instagram\.com\/([^/?&#]+)/);
        return match ? `@${match[1]}` : null;
      }
      
      // Remove instagram.com/
      cleaned = cleaned.replace('instagram.com/', '');
      
      // Get the username (everything before /, ?, &, or #)
      const username = cleaned.split(/[/?&#]/)[0];
      
      // Return with @ prefix if we have a valid username
      return username ? `@${username}` : null;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let e = params.get('email') || '';
    if (!e) {
      try { e = localStorage.getItem('postaway_email') || ''; } catch (_) {}
    }
    setEmail(e);
    if (!e) {
      console.warn('TravelBoard: missing email; append ?email=you@example.com to URL');
    }
    
    // Get current user ID from token
    const token = getAuthToken();
    if (token) {
      const decoded = decodeToken(token);
      if (decoded && decoded.userId) {
        setCurrentUserId(decoded.userId);
      }
    }
    
    (async () => {
      try {
        const res = await fetch('/api/access/subscription', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: e }) });
        const data = await res.json();
        if (res.ok && data.ok && data.active) {
          setMembershipTier(data.membershipTier || 'all_access');
          if (data.membershipTier === 'basic') {
            setAuthorized('basic');
            setUserRoles(data.roles || []);
          } else {
            setAuthorized(true);
            setUserRoles(data.roles || []);
            const canUserPost = (data.roles || []).some(r => r === 'creator' || r === 'host');
            setCanPost(canUserPost);
          }
        } else {
          setAuthorized(false);
        }
      } catch (err) {
        setAuthorized(false);
      }
    })();
  }, []);

  // Function to fetch travel board data
  const fetchTravelBoard = async (search = '') => {
    if (authorized !== true || !email) return;
    try {
      const res = await fetch('/api/travel/board', { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        body: JSON.stringify({ email, search }) 
      });
      const json = await res.json();
      if (!res.ok) {
        console.error('TravelBoard fetch error', res.status, json);
      }
      if (res.ok && json.ok) {
        const hosts = json.hosts || [];
        const creators = json.creators || [];
        const isDefaultLoad = !search || !search.trim();
        setData({
          hosts: isDefaultLoad ? shuffleArray(hosts) : hosts,
          creators: isDefaultLoad ? shuffleArray(creators) : creators,
        });
        setHostStartIndex(0);
        setCreatorStartIndex(0);
      }
    } catch (_) {}
  };

  // Handle like toggle
  const handleLikeToggle = (isLiked, likeCount, travelPostId) => {
    setData(prevData => {
      const updateItem = (item) => {
        if (item.travel_post_id === travelPostId) {
          return { ...item, is_liked: isLiked, like_count: likeCount };
        }
        return item;
      };
      
      return {
        hosts: prevData.hosts.map(updateItem),
        creators: prevData.creators.map(updateItem)
      };
    });
  };

  // Handle search
  const handleSearch = () => {
    setSearchTerm(searchInput);
    fetchTravelBoard(searchInput);
  };

  // Handle Enter key press in search input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    fetchTravelBoard('');
  };

  // Initial load when authorized
  useEffect(() => {
    if (authorized === true && email) {
      fetchTravelBoard('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, email]);

  // Handle deep-linking from URL params (commentId or travelPostId)
  useEffect(() => {
    const handleDeepLink = async () => {
      const params = new URLSearchParams(window.location.search);
      const commentId = params.get('commentId');
      const travelPostId = params.get('travelPostId');
      
      if ((!commentId && !travelPostId) || (data.hosts.length === 0 && data.creators.length === 0)) {
        return;
      }

      if (commentId) {
        setFocusCommentId(commentId);
      }

      try {
        let targetTravelPostId = travelPostId;
        
        // If we have a commentId, resolve it to get the travel_post_id
        if (commentId && !travelPostId) {
          const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
          const res = await fetch(`${base}/api/board/comments/resolve?commentId=${encodeURIComponent(commentId)}`);
          const result = await res.json();
          
          if (!result.ok || !result.target || result.target.kind !== 'travel_post') {
            return;
          }
          targetTravelPostId = result.target.id;
        }

        if (!targetTravelPostId) return;

        // Find the travel post in hosts or creators
        const hostPost = data.hosts.find(h => h.travel_post_id === targetTravelPostId);
        const creatorPost = data.creators.find(c => c.travel_post_id === targetTravelPostId);

        if (hostPost) {
          const heroImg = hostPost.travel_details?.media?.heroImageUrl;
          const rawUrl = heroImg || hostPost.property_image_url;
          const isLocal = window.location.hostname === 'localhost';
          let resolved = rawUrl || '';
          if (resolved.startsWith('/') && isLocal) {
            resolved = resolved.startsWith('/uploads/') ? `http://localhost:4000${resolved}` : resolved;
          }
          
          setSelectedHost({ 
            ...hostPost, 
            description: hostPost.description,
            travelPost: hostPost.travel_post,
            travelPostId: hostPost.travel_post_id,
            travelDetails: hostPost.travel_details,
            image: resolved,
            plan_id: hostPost.plan_id,
            plan_name: hostPost.plan_name,
            handle: hostPost.handle,
            property_id: hostPost.property_id,
            user_id: hostPost.user_id,
            airbnb_url: hostPost.airbnb_url,
            vrbo_url: hostPost.vrbo_url,
            direct_booking_url: hostPost.direct_booking_url,
            founder: hostPost.founder
          });
          setModalOpen(true);
        } else if (creatorPost) {
          const rawUrl = creatorPost.avatar_asset_url || creatorPost.avatar_url;
          const isLocal = window.location.hostname === 'localhost';
          let resolved = rawUrl || '';
          if (resolved.startsWith('/') && isLocal) {
            resolved = resolved.startsWith('/uploads/') ? `http://localhost:4000${resolved}` : resolved;
          }
          
          const deepHeroImg = creatorPost.travel_details?.media?.heroImageUrl;
          const instagramHandle = creatorPost.instagram_url?.match(/instagram\.com\/([^/?&#]+)/)?.[1];
          const deepDetails = creatorPost.travel_details || {};
          const deepPostLocation = deepDetails.groupTrip?.location
            || deepDetails.location
            || deepDetails.uniqueDestination?.recommendation
            || null;
          const deepCreatorHandle = creatorPost.handle ? fmtHandle(creatorPost.handle) : (instagramHandle ? `@${instagramHandle}` : (creatorPost.first_name || '@creator'));
          const displayHandle = deepPostLocation || deepCreatorHandle;
          
          setSelectedHost({ 
            property_name: displayHandle,
            travelPost: creatorPost.travel_post,
            travelPostId: creatorPost.travel_post_id,
            travelPostType: creatorPost.travel_post_type,
            travelDetails: creatorPost.travel_details,
            travel_post_expires_at: creatorPost.travel_post_expires_at,
            image: deepHeroImg || resolved,
            plan_id: creatorPost.plan_id,
            plan_name: creatorPost.plan_name,
            user_id: creatorPost.user_id,
            handle: creatorPost.handle,
            profile_id: creatorPost.profile_id,
            isCreator: true,
            founder: creatorPost.founder
          });
          setModalOpen(true);
        }
      } catch (err) {
        console.error('Error handling deep link:', err);
      }
    };

    handleDeepLink();
  }, [data.hosts, data.creators]);

  // Load only this user's properties if they are a host
  useEffect(() => {
    const loadUserProperties = async () => {
      if (authorized !== true || !email || !userRoles.includes('host')) return;
      try {
        const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
        const res = await fetch(`${base}/api/properties/list?email=${encodeURIComponent(email)}`);
        const j = await res.json();
        if (res.ok && j.ok) {
          setUserProperties(j.properties || []);
        }
      } catch (_) {}
    };
    loadUserProperties();
  }, [authorized, email, userRoles]);

  // Realtime search with debouncing
  useEffect(() => {
    // Skip if not authorized or searchInput hasn't changed
    if (authorized !== true || !email || searchInput === searchTerm) return;
    
    const delayDebounceFn = setTimeout(() => {
      setSearchTerm(searchInput);
      fetchTravelBoard(searchInput);
    }, 400); // 400ms delay

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput, authorized, email]);

  if (authorized === null) {
    return <InfoPage belowHeader={<div style={{ textAlign: 'center', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", color: '#0f084b' }}>Checking access…</div>} disableStamps tallOverride={false} customMinHeight={600} showInlineLivePulse />;
  }

  if (!authorized) {
    return <InfoPage belowHeader={<div style={{ textAlign: 'center', fontFamily: "'Neue Einstellung', 'Poppins', sans-serif", color: '#0f084b' }}>
      Access requires an active subscription. <a href="/success" style={{ color: '#dc1d08', textDecoration: 'none', fontWeight: 700 }}>Subscribe</a>
    </div>} disableStamps tallOverride={false} customMinHeight={600} showInlineLivePulse />;
  }

  if (authorized === 'basic' || membershipTier === 'basic') {
    const userType = userRoles.includes('host') ? 'host' : (userRoles.includes('traveler') ? 'traveler' : 'host');
    return (
      <InfoPage below={
        <BlurredGate userType={userType}>
          <div className="travel-board-page">
            <div className="travel-board-description"><p>A place for creators to share group trips and unique destinations and hosts to list last minute availability/discounted stays</p></div>
            <div className="travel-board-container">
              <div className="travel-board-section">
                <div className="travel-board-section-header"><div className="travel-board-section-title">Our Hosts</div></div>
                <div className="travel-board-carousel-grid" style={{ minHeight: 300 }} />
              </div>
              <div className="travel-board-section">
                <div className="travel-board-section-header"><div className="travel-board-section-title">Our Creators</div></div>
                <div className="travel-board-carousel-grid" style={{ minHeight: 300 }} />
              </div>
            </div>
          </div>
        </BlurredGate>
      } disableStamps tallOverride={false} customMinHeight={720} showInlineLivePulse />
    );
  }

  // No additional header; brand/title already shown in PostcardHero
  
  // Navigation handlers
  const handleHostPrev = () => {
    if (hostStartIndex > 0) {
      setHostDirection(-1);
      setHostStartIndex(hostStartIndex - 1);
    }
  };

  const handleHostNext = () => {
    if (hostStartIndex + 5 < data.hosts.length) {
      setHostDirection(1);
      setHostStartIndex(hostStartIndex + 1);
    }
  };

  const handleCreatorPrev = () => {
    if (creatorStartIndex > 0) {
      setCreatorDirection(-1);
      setCreatorStartIndex(creatorStartIndex - 1);
    }
  };

  const handleCreatorNext = () => {
    if (creatorStartIndex + 5 < data.creators.length) {
      setCreatorDirection(1);
      setCreatorStartIndex(creatorStartIndex + 1);
    }
  };

  const below = (
    <div className="travel-board-page">
      {/* Add Travel Opportunity Button */}
      {(userRoles.includes('host') || userRoles.includes('creator')) && (
        <div className="travel-board-add-button">
          <button
            className="button"
            type="button"
            onClick={() => {
              const eligibleRoles = userRoles.filter(role => role === 'host' || role === 'creator');
              const type = eligibleRoles.includes(activePersona) ? activePersona : eligibleRoles[0];
              setUserType(type);
              setPostText('');
              setPostPropertyId('');
              // Reset creator travel post fields
              setTravelPostType('');
              setAdditionalNotes('');
              setGroupTripLocation('');
              setGroupTripDateStart('');
              setGroupTripDateEnd('');
              setGroupTripSpots('');
              setGroupTripPriceMin('');
              setGroupTripPriceMax('');
              setGroupTripHostedCount('');
              setGroupTripContact('');
              setGroupTripDurationDays('');
              setGroupTripSignupUrl('');
              setDestinationRecommendation('');
              setFollowMeCta('');
              setStorefrontUrl('');
              // Reset media fields
              setHeroImageFile(null);
              setHeroImagePreview('');
              setHeroImageUrl('');
              setReelVideoFile(null);
              setReelVideoPreview('');
              setReelVideoUrl('');
              setExternalUrl('');
              setMediaSourceType('link');
              setUploadProgress('');
              // Reset edit mode for new post
              setIsEditMode(false);
              setEditPostId(null);
              setPostExpirationType('indefinitely');
              setPostExpirationDate('');
              setPostModalOpen(true);
            }}
            style={{ cursor: 'pointer' }}
          >
            {addButtonLabel}
          </button>
        </div>
      )}
      
      {/* Active Filter Banner */}
      {activeFilter && filterLabel && (
        <div className="travel-board-filter-banner">
          <span className="travel-board-filter-label">Showing: <strong>{filterLabel}</strong></span>
          <a href="#" onClick={(e) => { e.preventDefault(); setSelectedCategory(''); }} className="travel-board-filter-clear">View All</a>
        </div>
      )}
      
      {/* Description text */}
      <div className="travel-board-description">
        <p>
          A place for creators to share group trips and unique destinations and hosts to list last minute availability/discounted stays
        </p>
      </div>
      
      {/* Search Bar and Category Filter */}
      <div className="travel-board-search-container">
        <div className="travel-board-search-wrapper">
          <input
            type="text"
            placeholder="Search creators and hosts..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleKeyPress}
            className="travel-board-search-input"
            style={{ paddingRight: searchInput ? '40px' : '16px' }}
            onFocus={(e) => e.target.style.borderColor = '#141416'}
            onBlur={(e) => e.target.style.borderColor = '#E6E8EC'}
          />
          {searchInput && (
            <button
              onClick={handleClearSearch}
              className="travel-board-search-clear"
            >
              ×
            </button>
          )}
        </div>
        <div className="travel-board-filter-wrapper">
          <Dropdown
            className="travel-board-category-dropdown"
            value={selectedCategoryLabel}
            setValue={handleCategoryChange}
            options={categoryOptions}
          />
        </div>
      </div>
      {searchTerm && (
        <div className="travel-board-search-info">
          <p>
            Searching for "{searchTerm}"
          </p>
        </div>
      )}
      
      <div className="travel-board-container">
        {/* Filtered Results Section */}
        {activeFilter && filterLabel && (
          <div className="travel-board-section travel-board-filtered-section">
            <div className="travel-board-section-header">
              <div className="travel-board-section-title">{filterLabel}</div>
            </div>
            {filteredItems.length === 0 ? (
              <div className="travel-board-empty-filter">
                <p>No {filterLabel.toLowerCase()} posted yet. Be the first to add one!</p>
              </div>
            ) : (
              <div className="travel-board-carousel-grid view-all-grid">
                {filteredItems.map((item, i) => {
                  const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';

                  if (item.isHost) {
                    const heroImg = item.travel_details?.media?.heroImageUrl;
                    let resolved = heroImg || item.property_image_url || '';
                    if (resolved.startsWith('/') && isLocal) {
                      resolved = resolved.startsWith('/uploads/') ? `http://localhost:4000${resolved}` : resolved;
                    }
                    const propertyDisplay = item.brand_name
                      ? `${item.brand_name}${item.property_name ? ` – ${item.property_name}` : ''}`
                      : (item.property_name || fmtHandle(item.handle || 'Host'));
                    return (
                      <Card
                        key={`filtered-host-${i}`}
                        img={resolved}
                        handle={propertyDisplay}
                        likeCount={item.like_count || 0}
                        isLiked={!!item.is_liked}
                        travelPostId={item.travel_post_id}
                        introVideoUrl={item.property_intro_video_url || item.intro_video_url}
                        introVideoPosterUrl={item.property_intro_video_poster_url || item.intro_video_poster_url}
                        introVideoPublicId={item.property_intro_video_public_id || item.intro_video_public_id}
                        onLikeToggle={(id, newLiked, newCount) => {
                          setData(prev => ({
                            hosts: prev.hosts.map(h => h.travel_post_id === id ? { ...h, is_liked: newLiked, like_count: newCount } : h),
                            creators: prev.creators.map(c => c.travel_post_id === id ? { ...c, is_liked: newLiked, like_count: newCount } : c),
                          }));
                        }}
                        onClick={() => {
                          setSelectedHost({
                            ...item,
                            description: item.description,
                            travelPost: item.travel_post,
                            travelPostId: item.travel_post_id,
                            travelDetails: item.travel_details,
                            image: resolved,
                            plan_id: item.plan_id,
                            plan_name: item.plan_name,
                            handle: item.handle,
                            property_id: item.property_id,
                            user_id: item.user_id,
                            airbnb_url: item.airbnb_url,
                            vrbo_url: item.vrbo_url,
                            direct_booking_url: item.direct_booking_url,
                            founder: item.founder,
                          });
                          setModalOpen(true);
                        }}
                      />
                    );
                  }

                  // Creator item
                  let resolved = item.avatar_asset_url || item.avatar_url || '';
                  if (resolved.startsWith('/') && isLocal) {
                    resolved = resolved.startsWith('/uploads/') ? `http://localhost:4000${resolved}` : resolved;
                  }
                  const heroImg = item.travel_details?.media?.heroImageUrl;
                  const cardImg = heroImg || resolved;
                  const instagramHandle = extractInstagramHandle(item.instagram_url);

                  // Show property/location from the post details, not the creator's personal handle
                  const details = item.travel_details || {};
                  const postLocation = details.groupTrip?.location
                    || details.location
                    || details.uniqueDestination?.recommendation
                    || null;
                  const creatorHandle = item.handle ? fmtHandle(item.handle) : (instagramHandle || (item.first_name ? item.first_name : '@creator'));
                  const displayHandle = postLocation || creatorHandle;
                  return (
                    <Card
                      key={`filtered-creator-${i}`}
                      img={cardImg}
                      handle={displayHandle}
                      likeCount={item.like_count || 0}
                      isLiked={!!item.is_liked}
                      travelPostId={item.travel_post_id}
                      introVideoUrl={item.intro_video_url}
                      introVideoPosterUrl={item.intro_video_poster_url}
                      introVideoPublicId={item.intro_video_public_id}
                      onLikeToggle={(id, newLiked, newCount) => {
                        setData(prev => ({
                          hosts: prev.hosts.map(h => h.travel_post_id === id ? { ...h, is_liked: newLiked, like_count: newCount } : h),
                          creators: prev.creators.map(c => c.travel_post_id === id ? { ...c, is_liked: newLiked, like_count: newCount } : c),
                        }));
                      }}
                      onClick={item.travel_post ? () => {
                        setSelectedHost({
                          property_name: displayHandle,
                          travelPost: item.travel_post,
                          travelPostId: item.travel_post_id,
                          travelPostType: item.travel_post_type,
                          travelDetails: item.travel_details,
                          travel_post_expires_at: item.travel_post_expires_at,
                          image: heroImg || resolved,
                          plan_id: item.plan_id,
                          plan_name: item.plan_name,
                          user_id: item.user_id,
                          handle: item.handle,
                          profile_id: item.profile_id,
                          isCreator: true,
                          founder: item.founder,
                        });
                        setModalOpen(true);
                      } : null}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
        
        <div className="travel-board-section">
          <div className="travel-board-section-header">
            <div className="travel-board-section-title">Our Hosts</div>
            {data.hosts.filter(property => property.property_image_url).length > 5 && (
              <button
                type="button"
                className="travel-board-view-all-btn"
                aria-expanded={showAllHosts}
                aria-controls="travel-hosts-grid"
                onClick={() => setShowAllHosts(!showAllHosts)}
              >
                {showAllHosts ? 'Show Less' : 'View All'}
              </button>
            )}
          </div>
          <div className={`travel-board-carousel-container${showAllHosts ? ' view-all' : ''}`}>
            {!showAllHosts && (
              <AnimatePresence>
                <NavigationButton 
                  direction="left" 
                  onClick={handleHostPrev} 
                  show={data.hosts.length > 5 && hostStartIndex > 0}
                />
                <NavigationButton 
                  direction="right" 
                  onClick={handleHostNext} 
                  show={data.hosts.length > 5 && hostStartIndex + 5 < data.hosts.length}
                />
              </AnimatePresence>
            )}
            <AnimatePresence mode="wait">
              <motion.div
                key={showAllHosts ? 'all' : hostStartIndex}
                id="travel-hosts-grid"
                initial={showAllHosts ? { opacity: 0.8 } : { x: hostDirection === -1 ? -30 : 30, opacity: 0.8 }}
                animate={{ x: 0, opacity: 1 }}
                exit={showAllHosts ? { opacity: 0.8 } : { x: hostDirection === -1 ? 30 : -30, opacity: 0.8 }}
                transition={{ duration: 0.25, ease: "easeInOut" }}
                className="travel-board-carousel-grid"
              >
              {(showAllHosts
                ? data.hosts.filter(property => property.property_image_url || property.travel_details?.media?.heroImageUrl)
                : data.hosts.filter(property => property.property_image_url || property.travel_details?.media?.heroImageUrl).slice(hostStartIndex, hostStartIndex + 5)
              ).map((property, i) => {
              const heroImg = property.travel_details?.media?.heroImageUrl;
              const rawUrl = heroImg || property.property_image_url;
              const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
              let resolved = rawUrl || '';
              if (resolved.startsWith('/')) {
                if (isLocal) {
                  resolved = resolved.startsWith('/uploads/') ? `http://localhost:4000${resolved}` : resolved;
                }
                // Don't fallback to founders.png - filter out properties without images
              }
              // Don't resolve to founders.png - only show properties with valid images
              if (!resolved) return null;
              
              // Display property name (not handle) to match Collab and Hosts boards
              const propertyDisplay = property.property_name || 
                                     (() => {
                                       const parts = [];
                                       if (property.location_city) parts.push(property.location_city);
                                       if (property.location_state) parts.push(property.location_state);
                                       if (property.location_country && property.location_country !== 'United States') {
                                         parts.push(property.location_country);
                                       }
                                       return parts.join(', ') || 'Property';
                                     })();
              
              return (
                <Card 
                  key={`property-${property.property_id || hostStartIndex + i}`} 
                  img={resolved} 
                  handle={propertyDisplay} 
                  likeCount={property.like_count || 0}
                  isLiked={property.is_liked || false}
                  travelPostId={property.travel_post_id}
                  introVideoUrl={property.property_intro_video_url || property.intro_video_url}
                  introVideoPosterUrl={property.property_intro_video_poster_url || property.intro_video_poster_url}
                  introVideoPublicId={property.property_intro_video_public_id || property.intro_video_public_id}
                  onLikeToggle={(liked, count) => handleLikeToggle(liked, count, property.travel_post_id)}
                  onClick={() => {
                    setSelectedHost({ 
                      ...property, 
                      description: property.description,
                      travelPost: property.travel_post,
                      travelPostId: property.travel_post_id,
                      travelDetails: property.travel_details,
                      image: resolved,
                      plan_id: property.plan_id,
                      plan_name: property.plan_name,
                      handle: property.handle,
                      property_id: property.property_id,
                      user_id: property.user_id,
                      airbnb_url: property.airbnb_url,
                      vrbo_url: property.vrbo_url,
                      direct_booking_url: property.direct_booking_url,
                      founder: property.founder
                    });
                    setModalOpen(true);
                  }}
                />
              );
              })}
            </motion.div>
            </AnimatePresence>
          </div>
        </div>
        <div className="travel-board-section">
          <div className="travel-board-section-header">
            <div className="travel-board-section-title">Our Creators</div>
            {data.creators.filter(c => c.avatar_asset_url || c.avatar_url).length > 5 && (
              <button
                type="button"
                className="travel-board-view-all-btn"
                aria-expanded={showAllCreators}
                aria-controls="travel-creators-grid"
                onClick={() => setShowAllCreators(!showAllCreators)}
              >
                {showAllCreators ? 'Show Less' : 'View All'}
              </button>
            )}
          </div>
          <div className={`travel-board-carousel-container${showAllCreators ? ' view-all' : ''}`}>
            {!showAllCreators && (
              <AnimatePresence>
                <NavigationButton 
                  direction="left" 
                  onClick={handleCreatorPrev} 
                  show={data.creators.length > 5 && creatorStartIndex > 0}
                />
                <NavigationButton 
                  direction="right" 
                  onClick={handleCreatorNext} 
                  show={data.creators.length > 5 && creatorStartIndex + 5 < data.creators.length}
                />
              </AnimatePresence>
            )}
            <AnimatePresence mode="wait">
              <motion.div 
                key={showAllCreators ? 'all' : creatorStartIndex}
                id="travel-creators-grid"
                initial={showAllCreators ? { opacity: 0.8 } : { x: creatorDirection === -1 ? -30 : 30, opacity: 0.8 }}
                animate={{ x: 0, opacity: 1 }}
                exit={showAllCreators ? { opacity: 0.8 } : { x: creatorDirection === -1 ? 30 : -30, opacity: 0.8 }}
                transition={{ duration: 0.25, ease: "easeInOut" }}
                className="travel-board-carousel-grid"
              >
              {(showAllCreators
                ? data.creators.filter(c => c.avatar_asset_url || c.avatar_url)
                : data.creators.filter(c => c.avatar_asset_url || c.avatar_url).slice(creatorStartIndex, creatorStartIndex + 5)
              ).map((c, i) => {
              const rawUrl = c.avatar_asset_url || c.avatar_url;
              const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
              let resolved = rawUrl || '';
              if (resolved.startsWith('/')) {
                if (isLocal) {
                  resolved = resolved.startsWith('/uploads/') ? `http://localhost:4000${resolved}` : resolved;
                }
              }
              if (!resolved) return null;

              // Prefer hero image from travel_details.media for the card display
              const heroImg = c.travel_details?.media?.heroImageUrl;
              const cardImg = heroImg || resolved;
              
              // Show property/location from the post details, not the creator's personal handle
              const instagramHandle = extractInstagramHandle(c.instagram_url);
              const cDetails = c.travel_details || {};
              const postLocation = cDetails.groupTrip?.location
                || cDetails.location
                || cDetails.uniqueDestination?.recommendation
                || null;
              const creatorHandle = c.handle ? fmtHandle(c.handle) : (instagramHandle || (c.first_name ? c.first_name : '@creator'));
              const displayHandle = postLocation || creatorHandle;
              
              return (
                <Card 
                  key={`c-${creatorStartIndex + i}`} 
                  img={cardImg} 
                  handle={displayHandle} 
                  likeCount={c.like_count || 0}
                  isLiked={c.is_liked || false}
                  travelPostId={c.travel_post_id}
                  introVideoUrl={c.intro_video_url}
                  introVideoPosterUrl={c.intro_video_poster_url}
                  introVideoPublicId={c.intro_video_public_id}
                  onLikeToggle={(liked, count) => handleLikeToggle(liked, count, c.travel_post_id)}
                  onClick={c.travel_post ? () => {
                    setSelectedHost({ 
                      property_name: displayHandle,
                      travelPost: c.travel_post,
                      travelPostId: c.travel_post_id,
                      travelPostType: c.travel_post_type,
                      travelDetails: c.travel_details,
                      travel_post_expires_at: c.travel_post_expires_at,
                      image: heroImg || resolved,
                      plan_id: c.plan_id,
                      plan_name: c.plan_name,
                      user_id: c.user_id,
                      handle: c.handle,
                      profile_id: c.profile_id,
                      isCreator: true,
                      founder: c.founder
                    });
                    setModalOpen(true);
                  } : null}
                />
              );
              })}
            </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <InfoPage below={below} disableStamps tallOverride={false} customMinHeight={720} showInlineLivePulse />
      <Modal 
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedHost(null);
          setFocusCommentId(null);
        }}
        title={
          selectedHost?.property_name || 
          (selectedHost?.brand_name ? 
            `${selectedHost.brand_name} - ${selectedHost.location_city || 'Property'}` : 
            'Property')
        }
        content={selectedHost?.description}
        image={selectedHost?.image ? getCloudinaryUrl(selectedHost.image, { width: 900, height: 600, crop: 'fill', quality: 'auto', format: 'auto' }) : null}
        planId={selectedHost?.plan_id}
        planName={selectedHost?.plan_name}
        messageUserId={selectedHost?.user_id}
        propertyId={selectedHost?.property_id || selectedHost?.id}
        travelPost={selectedHost?.travelPost}
        travelPostId={selectedHost?.travelPostId}
        travelPostType={selectedHost?.travelPostType}
        travelDetails={selectedHost?.travelDetails}
        isOwner={selectedHost?.user_id === currentUserId}
        handle={selectedHost?.handle}
        profile_id={selectedHost?.profile_id}
        host_profile_id={selectedHost?.host_profile_id}
        isCreator={selectedHost?.isCreator}
        airbnbUrl={selectedHost?.airbnb_url}
        vrboUrl={selectedHost?.vrbo_url}
        directBookingUrl={selectedHost?.direct_booking_url}
        founder={selectedHost?.founder}
        focusCommentId={focusCommentId}
        onEdit={() => {
          // Close details modal
          setModalOpen(false);
          
          // Determine if this is a host or creator post
          const isCreatorPost = selectedHost?.isCreator || false;
          const type = isCreatorPost ? 'creator' : 'host';
          
          // Prefill form fields from selectedHost
          setUserType(type);
          setPostPropertyId(selectedHost?.property_id || '');
          setPostText(selectedHost?.travelPost || '');
          
          // Prefill travel details
          const details = selectedHost?.travelDetails || {};

          setAdditionalNotes(details.additionalNotes || '');
          
          // Travel post type (for creators)
          setTravelPostType(selectedHost?.travelPostType || '');
          
          // Group Trip fields
          if (details.groupTrip) {
            setGroupTripLocation(details.groupTrip.location || '');
            setGroupTripDateStart(details.groupTrip.dateStart || '');
            setGroupTripDateEnd(details.groupTrip.dateEnd || '');
            setGroupTripSpots(details.groupTrip.availableSpots || '');
            setGroupTripPriceMin(details.groupTrip.priceMin || '');
            setGroupTripPriceMax(details.groupTrip.priceMax || '');
            setGroupTripHostedCount(details.groupTrip.hostedCount || '');
            setGroupTripContact(details.groupTrip.contact || '');
            setGroupTripDurationDays(details.groupTrip.durationDays || '');
            setGroupTripSignupUrl(details.groupTrip.signupUrl || '');
          } else {
            setGroupTripLocation('');
            setGroupTripDateStart('');
            setGroupTripDateEnd('');
            setGroupTripSpots('');
            setGroupTripPriceMin('');
            setGroupTripPriceMax('');
            setGroupTripHostedCount('');
            setGroupTripContact('');
            setGroupTripDurationDays('');
            setGroupTripSignupUrl('');
          }
          
          // Unique Destination fields
          if (details.uniqueDestination) {
            setDestinationRecommendation(details.uniqueDestination.recommendation || '');
          } else {
            setDestinationRecommendation('');
          }
          
          // Follow Me fields
          if (details.followMe) {
            setFollowMeCta(details.followMe.cta || '');
          } else {
            setFollowMeCta('');
          }

          // Storefront fields
          if (details.storefront) {
            setStorefrontUrl(details.storefront.url || '');
          } else {
            setStorefrontUrl('');
          }
          
          // Media fields
          const media = details.media || {};
          setHeroImageUrl(media.heroImageUrl || '');
          setHeroImagePreview(media.heroImageUrl || '');
          setHeroImageFile(null);
          setReelVideoUrl(media.reelVideoUrl || '');
          setReelVideoPreview(media.reelVideoUrl || '');
          setReelVideoFile(null);
          setExternalUrl(media.externalUrl || '');
          setMediaSourceType(media.reelVideoUrl ? 'upload' : 'link');
          setUploadProgress('');
          
          // Set edit mode and store the post ID
          setIsEditMode(true);
          setEditPostId(selectedHost?.travelPostId || null);
          
          // Pre-fill expiration date
          const expiresAt = selectedHost?.travel_post_expires_at;
          if (expiresAt) {
            setPostExpirationType('date');
            setPostExpirationDate(new Date(expiresAt).toISOString().split('T')[0]);
          } else {
            setPostExpirationType('indefinitely');
            setPostExpirationDate('');
          }
          
          setPostModalOpen(true);
        }}
        onDelete={async () => {
          try {
            const token = getAuthToken();
            if (!token) {
              alert('Please log in to delete this opportunity');
              return;
            }
            
            const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const params = new URLSearchParams();
            params.set('postId', selectedHost.travelPostId);
            
            const res = await authFetch(`${base}/api/travel/delete?${params.toString()}`, {
              method: 'DELETE'
            });
            
            const data = await res.json();
            if (res.ok && data.ok) {
              setModalOpen(false);
              setSelectedHost(null);
              // Refresh the board
              await fetchTravelBoard(searchTerm || '');
            } else {
              alert('Failed to delete travel opportunity');
            }
          } catch (err) {
            // Don't log auth errors since authFetch handles logout
            if (!err.message?.includes('Unauthorized') && !err.message?.includes('expired')) {
              console.error('Delete failed:', err);
              alert('Failed to delete travel opportunity');
            }
          }
        }}
      />
      {/* Post Travel Modal */}
      {postModalOpen && (
        <div 
          className="travel-post-modal-overlay"
          onClick={() => {
            setPostModalOpen(false);
            setIsEditMode(false);
            setEditPostId(null);
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="travel-post-modal-content travel-post-modal-enhanced"
          >
            <h3>{isEditMode ? 'Edit Travel Opportunity' : 'Add a Travel Opportunity'}</h3>
            <div className="travel-post-scrollable">
              {/* Host-only: Property selection */}
              {userType === 'host' && (
                <div className="travel-post-field">
                  <label>Which property is this opportunity available for?</label>
                  <select value={postPropertyId} onChange={(e) => setPostPropertyId(e.target.value)}>
                    <option value="">Select a property…</option>
                    {userProperties.map((p) => (
                      <option key={p.id} value={p.id}>{p.name || 'Property'}{(() => {
                        const parts = [];
                        if (p.location_city) parts.push(p.location_city);
                        if (p.location_state) parts.push(p.location_state);
                        if (p.location_country && p.location_country !== 'United States') {
                          parts.push(p.location_country);
                        }
                        return parts.length ? ` — ${parts.join(', ')}` : '';
                      })()}</option>
                    ))}
                  </select>
                  {userProperties.length === 0 && (
                    <div className="travel-post-no-properties">
                      You have no properties yet. Add one from your dashboard first.
                    </div>
                  )}
                </div>
              )}

              {/* Type of Announcement selection — shown for both hosts and creators */}
              <div className="travel-post-field">
                <label>Type of Announcement</label>
                <select value={travelPostType} onChange={(e) => setTravelPostType(e.target.value)}>
                    <option value="">Select type…</option>
                    <option value="group_trip">Group Trip</option>
                    <option value="substack">Substack / Newsletter / Blog</option>
                    <option value="podcast">Podcast</option>
                    <option value="upcoming_event">Upcoming Event</option>
                    <option value="last_minute">Last Minute Availability</option>
                    <option value="discount_code">Discount Codes</option>
                    <option value="unique_destination">Unique Destination</option>
                    <option value="follow_me">Follow Me (Intro)</option>
                    <option value="general">General Announcement</option>
                </select>
              </div>

              {/* Description/Overview - shown for both */}
              <div className="travel-post-field">
                <label>Description / Overview</label>
                <textarea
                  value={postText}
                  onChange={(e) => setPostText(e.target.value)}
                  placeholder={userType === 'creator' 
                    ? "Tell us about a group trip you are hosting, a unique destination or just a shout out to follow you on socials. This page can be seen by creators, hosts and travelers."
                    : "Announce last minute availability, a custom discount code if booking directly (i.e. POSTAWAY20) or any fun events like music festivals or Oktoberfest in your area…"}
                  rows={4}
                />
              </div>

              {/* Type-specific fields + shared media */}
              {travelPostType && (
                <>
                  {/* GROUP TRIP fields */}
                  {travelPostType === 'group_trip' && (
                    <>
                      <div className="travel-post-field">
                        <label>Location for Upcoming Group Trip</label>
                        <input
                          type="text"
                          value={groupTripLocation}
                          onChange={(e) => setGroupTripLocation(e.target.value)}
                          placeholder="Where is the trip?"
                        />
                      </div>
                      <div className="travel-post-field">
                        <label>Dates for Upcoming Group Trip</label>
                        <div className="travel-post-date-range">
                          <input type="date" value={groupTripDateStart} onChange={(e) => setGroupTripDateStart(e.target.value)} />
                          <span>to</span>
                          <input type="date" value={groupTripDateEnd} onChange={(e) => setGroupTripDateEnd(e.target.value)} />
                        </div>
                      </div>
                      <div className="travel-post-field">
                        <label>How many available spots?</label>
                        <input
                          type="number"
                          value={groupTripSpots}
                          onChange={(e) => setGroupTripSpots(e.target.value)}
                          placeholder="e.g., 10"
                          min="1"
                        />
                      </div>
                      <div className="travel-post-field">
                        <label>Price Range per person</label>
                        <div className="travel-post-price-range">
                          <input
                            type="number"
                            value={groupTripPriceMin}
                            onChange={(e) => setGroupTripPriceMin(e.target.value)}
                            placeholder="Min $"
                          />
                          <span>to</span>
                          <input
                            type="number"
                            value={groupTripPriceMax}
                            onChange={(e) => setGroupTripPriceMax(e.target.value)}
                            placeholder="Max $"
                          />
                        </div>
                      </div>
                      <div className="travel-post-field">
                        <label>How many group trips have you hosted?</label>
                        <input
                          type="number"
                          value={groupTripHostedCount}
                          onChange={(e) => setGroupTripHostedCount(e.target.value)}
                          placeholder="e.g., 5"
                          min="0"
                        />
                      </div>
                      <div className="travel-post-field">
                        <label>For more information, where should interested travelers contact you?</label>
                        <input
                          type="text"
                          value={groupTripContact}
                          onChange={(e) => setGroupTripContact(e.target.value)}
                          placeholder="Email, Instagram, website, etc."
                        />
                      </div>
                      <div className="travel-post-field">
                        <label>Duration (Days)</label>
                        <input
                          type="number"
                          value={groupTripDurationDays}
                          onChange={(e) => setGroupTripDurationDays(e.target.value)}
                          placeholder="e.g., 7"
                          min="1"
                        />
                      </div>
                      <div className="travel-post-field">
                        <label>Upload link for sign up</label>
                        <input
                          type="url"
                          value={groupTripSignupUrl}
                          onChange={(e) => setGroupTripSignupUrl(e.target.value)}
                          placeholder="https://your-signup-link.com"
                        />
                      </div>
                    </>
                  )}

                  {/* UNIQUE DESTINATION fields */}
                  {travelPostType === 'unique_destination' && (
                    <div className="travel-post-field">
                      <label>Tell us about some places you've recently gone or would highly recommend</label>
                      <textarea
                        value={destinationRecommendation}
                        onChange={(e) => setDestinationRecommendation(e.target.value)}
                        placeholder="Share your recommendations..."
                        rows={4}
                      />
                    </div>
                  )}

                  {/* FOLLOW ME fields */}
                  {travelPostType === 'follow_me' && (
                    <div className="travel-post-field">
                      <label>Follow me for more travel content...</label>
                      <textarea
                        value={followMeCta}
                        onChange={(e) => setFollowMeCta(e.target.value)}
                        placeholder="Where can people follow you? Share your social handles, website, etc."
                        rows={3}
                      />
                    </div>
                  )}

                  {/* STOREFRONT / LINKTREE fields */}
                  {travelPostType === 'storefront' && (
                    <div className="travel-post-field">
                      <label>Link to your Storefront, Linktree, LTK, ShopMy, etc.</label>
                      <input
                        type="url"
                        value={storefrontUrl}
                        onChange={(e) => setStorefrontUrl(e.target.value)}
                        placeholder="https://linktr.ee/yourname, https://www.shopmy.us/..., https://www.shopltk.com/..."
                      />
                    </div>
                  )}
                </>
              )}

              {/* SHARED MEDIA: Hero Image + Reel/Link — shown for both hosts and creators */}
              {(userType === 'host' || travelPostType) && (
                <>
                  <div className="travel-post-field">
                    <label>Upload Hero Image</label>
                    {heroImagePreview && (
                      <div className="travel-post-media-preview">
                        <img src={heroImagePreview} alt="Hero preview" style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, objectFit: 'cover', marginBottom: 8 }} />
                        <button type="button" className="travel-post-media-remove" onClick={() => { setHeroImageFile(null); setHeroImagePreview(''); setHeroImageUrl(''); }}>Remove</button>
                      </div>
                    )}
                    {!heroImagePreview && (
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,image/heif"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 10 * 1024 * 1024) {
                              alert('Image must be less than 10MB');
                              return;
                            }
                            setHeroImageFile(file);
                            const reader = new FileReader();
                            reader.onloadend = () => setHeroImagePreview(reader.result);
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    )}
                  </div>

                  <div className="travel-post-field">
                    <label>Add a Reel or Link</label>
                    <div className="travel-post-media-toggle">
                      <label className="travel-post-radio">
                        <input type="radio" name="mediaSource" value="link" checked={mediaSourceType === 'link'} onChange={() => setMediaSourceType('link')} />
                        <span>External Link</span>
                      </label>
                      <label className="travel-post-radio">
                        <input type="radio" name="mediaSource" value="upload" checked={mediaSourceType === 'upload'} onChange={() => setMediaSourceType('upload')} />
                        <span>Upload Video</span>
                      </label>
                    </div>
                    {mediaSourceType === 'link' && (
                      <input
                        type="url"
                        value={externalUrl}
                        onChange={(e) => setExternalUrl(e.target.value)}
                        placeholder="Paste a link (Instagram reel, YouTube, TikTok, storefront, podcast, etc.)"
                      />
                    )}
                    {mediaSourceType === 'upload' && (
                      <>
                        {reelVideoPreview && (
                          <div className="travel-post-media-preview">
                            <video src={reelVideoPreview} controls style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, marginBottom: 8 }} />
                            <button type="button" className="travel-post-media-remove" onClick={() => { setReelVideoFile(null); setReelVideoPreview(''); setReelVideoUrl(''); }}>Remove</button>
                          </div>
                        )}
                        {!reelVideoPreview && (
                          <input
                            type="file"
                            accept="video/mp4,video/quicktime,video/webm,video/mov"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                if (file.size > 100 * 1024 * 1024) {
                                  alert('Video must be less than 100MB');
                                  return;
                                }
                                setReelVideoFile(file);
                                setReelVideoPreview(URL.createObjectURL(file));
                              }
                            }}
                          />
                        )}
                      </>
                    )}
                  </div>

                  {uploadProgress && (
                    <div className="travel-post-field" style={{ color: '#777E90', fontSize: 13 }}>
                      {uploadProgress}
                    </div>
                  )}
                </>
              )}

              {/* Additional Notes - shown for both */}
              <div className="travel-post-field">
                <label>Additional Notes</label>
                <textarea
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  placeholder="Any other information you'd like to share..."
                  rows={3}
                />
              </div>

              {/* Post Expiration */}
              <div className="travel-post-field">
                <label>How long would you like to keep this post on the Board?</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="postExpiration"
                      checked={postExpirationType === 'date'}
                      onChange={() => setPostExpirationType('date')}
                    />
                    Specific Expiration Date
                  </label>
                  {postExpirationType === 'date' && (
                    <input
                      type="date"
                      value={postExpirationDate}
                      onChange={(e) => setPostExpirationDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      style={{ marginLeft: '24px', maxWidth: '200px', padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px' }}
                    />
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="postExpiration"
                      checked={postExpirationType === 'indefinitely'}
                      onChange={() => { setPostExpirationType('indefinitely'); setPostExpirationDate(''); }}
                    />
                    Indefinitely
                  </label>
                </div>
              </div>
            </div>

            <div className="travel-post-actions">
              <button
                type="button"
                className="button-stroke"
                onClick={() => {
                  setPostModalOpen(false);
                  setIsEditMode(false);
                  setEditPostId(null);
                  setPostExpirationType('indefinitely');
                  setPostExpirationDate('');
                }}
                style={{ cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button"
                onClick={async () => {
                  try {
                    const base = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
                    
                    // Upload hero image if a new file was selected
                    let finalHeroImageUrl = heroImageUrl || '';
                    if (heroImageFile) {
                      setUploadProgress('Uploading hero image...');
                      finalHeroImageUrl = await uploadFile(heroImageFile, {
                        onProgress: (status) => setUploadProgress(status === 'Converting...' ? 'Converting image...' : 'Uploading hero image...')
                      });
                    }

                    // Upload reel video if a new file was selected
                    let finalReelVideoUrl = reelVideoUrl || '';
                    if (reelVideoFile) {
                      setUploadProgress('Uploading video...');
                      finalReelVideoUrl = await uploadFile(reelVideoFile, {
                        onProgress: () => setUploadProgress('Uploading video...')
                      });
                    }

                    setUploadProgress('');
                    
                    // Build travelDetails object
                    let travelDetails = null;
                    
                    const hasMedia = finalHeroImageUrl || finalReelVideoUrl || externalUrl;
                    
                    travelDetails = {
                      additionalNotes: additionalNotes || null,
                    };

                    if (hasMedia) {
                      travelDetails.media = {
                        heroImageUrl: finalHeroImageUrl || null,
                        reelVideoUrl: (mediaSourceType === 'upload' ? finalReelVideoUrl : null) || null,
                        externalUrl: (mediaSourceType === 'link' ? (externalUrl || null) : null),
                      };
                    }
                    
                    if (travelPostType === 'group_trip') {
                      travelDetails.groupTrip = {
                        location: groupTripLocation || null,
                        dateStart: groupTripDateStart || null,
                        dateEnd: groupTripDateEnd || null,
                        availableSpots: groupTripSpots || null,
                        priceMin: groupTripPriceMin || null,
                        priceMax: groupTripPriceMax || null,
                        hostedCount: groupTripHostedCount || null,
                        contact: groupTripContact || null,
                        durationDays: groupTripDurationDays || null,
                        signupUrl: groupTripSignupUrl || null,
                      };
                    } else if (travelPostType === 'unique_destination') {
                      travelDetails.uniqueDestination = {
                        recommendation: destinationRecommendation || null,
                      };
                    } else if (travelPostType === 'follow_me') {
                      travelDetails.followMe = {
                        cta: followMeCta || null,
                      };
                    } else if (travelPostType === 'storefront') {
                      travelDetails.storefront = {
                        url: storefrontUrl || null,
                      };
                    }
                    
                    if (!hasMedia && !additionalNotes && !travelDetails.groupTrip && !travelDetails.uniqueDestination && !travelDetails.followMe && !travelDetails.storefront) {
                      travelDetails = null;
                    }
                    
                    const payload = { 
                      email, 
                      userType, 
                      content: postText,
                      travelPostType: travelPostType || null,
                      travelDetails,
                      expiresAt: postExpirationType === 'date' && postExpirationDate ? postExpirationDate : null
                    };
                    
                    if (userType === 'host' && postPropertyId) {
                      payload.propertyId = postPropertyId;
                    }
                    
                    // Include postId when editing to update in place
                    if (isEditMode && editPostId) {
                      payload.postId = editPostId;
                    }
                    
                    const res = await fetch(`${base}/api/travel/post`, { 
                      method: 'POST', 
                      headers: { 'Content-Type': 'application/json' }, 
                      body: JSON.stringify(payload) 
                    });
                    const j = await res.json();
                    if (!res.ok || !j.ok) throw new Error('post_failed');
                    setPostModalOpen(false);
                    setIsEditMode(false);
                    setEditPostId(null);
                    // Refresh the board so the new item shows first
                    await fetchTravelBoard(searchTerm || '');
                  } catch (_) {
                    alert('Unable to post to travel board. Please try again.');
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                {isEditMode ? 'Save Changes' : 'Post'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TravelBoard;
